import { Table, type TableProps } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AssignButton } from '@client/components/buttons/AssignButton';
import { HideColumns } from '@client/components/table/HideColumns';
import { Toolbar } from '@client/components/Toolbar';
import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useAssignHandlers } from '@client/hooks/useAssignHandlers';
import { useAuth } from '@client/hooks/useAuth';
import { useColumnsToggle } from '@client/hooks/useColumnsToggle';
import { useTable } from '@client/hooks/useTable';
import styles from '@client/pages/features/features.module.less';
import { columnsMetadata } from '@client/pages/subscriptions/columns.metadata';
import { useSubscriptionQueries } from '@client/queries/useSubscriptionQueries';
import { assignStore, type TAssignedState } from '@client/store/assigned.store';
import { useDirty } from '@client/store/dirty.store';
import { useLoading } from '@client/store/loading.store';
import { useLocale } from '@client/store/locale.store';
import { indexable } from '@client/utils/table/table.util';
import { type ISubscription } from '@common/types';

import type { TColumns } from '@client/types/antd.type';
import type { User } from 'firebase/auth';

export const SubscriptionsDrawerContent: React.FC = () => {
  const { user, ability } = useAuth();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { setDirty } = useDirty();
  const { useActiveSubscriptions } = useSubscriptionQueries();

  const { data, refetch, isLoading, isFetching } = useActiveSubscriptions();

  const subscriptions = data?.subscriptions ?? [];
  const total = data?.total ?? 0;

  const { loading } = useLoading(isLoading || isFetching);

  const { onAssign, isAssigned } = useAssignHandlers<
    ISubscription,
    TAssignedState<ISubscription>
  >(subscriptions, assignStore);

  const {
    tableParams: { pagination },
    filteredInfo,
    sortedInfo,
    handleTableChange,
  } = useTable<ISubscription>(subscriptions, total, {
    pageSize: 9,
    syncUrl: false,
  });

  const disabled = ability.cannot(
    EAbilityActions.ASSIGN,
    `${EAbilitySubjects.FEATURE}.${EAbilitySubjects.SUBSCRIPTIONS}`,
  );

  const columns: TableProps<ISubscription>['columns'] = [
    ...columnsMetadata(
      {
        locale,
        filteredInfo,
        sortedInfo,
        entities: subscriptions,
      },
      ['title', 'status'],
    ),
    {
      title: t('table.actions'),
      align: 'center',
      fixed: 'right',
      width: 150,
      render(record: ISubscription) {
        return (
          <AssignButton
            loading={loading}
            disabled={disabled}
            behavior={{
              isAssigned: () => isAssigned(record),
              confirm: t('feature.msg.unAssignConfirm'),
            }}
            onClick={() => {
              onAssign(record);
              setDirty(true);
            }}
          />
        );
      },
    },
  ];

  const { filteredColumns, columnsList, selectedColumns, setSelectedColumns } =
    useColumnsToggle(columns as TColumns[]);

  const tableProps = {
    columns: filteredColumns as TColumns<ISubscription>[],
    pagination,
    scroll: { x: 650 },
    bordered: true,
    className: styles.gridList,
    dataSource: indexable<ISubscription>(subscriptions),
    loading,
    rowKey: (record: ISubscription) => record.id ?? `row-${Math.random()}`,
    onChange: handleTableChange,
    title: () => (
      <div className={styles.gridHeader}>
        <Toolbar<ISubscription[]>
          entities={subscriptions}
          onRefresh={refetch}
          subject={`${EAbilitySubjects.SUBSCRIPTIONS}.${user?.uid as User['uid']}`}
        >
          <HideColumns
            columnsList={columnsList}
            selectedColumns={selectedColumns}
            onChange={setSelectedColumns}
          />
        </Toolbar>
      </div>
    ),
    footer: () => (
      <div className={styles.gridFooter}>
        {t('table.total', { amount: subscriptions.length })}
      </div>
    ),
  };

  return <Table<ISubscription> {...tableProps} />;
};
